import * as d3 from "d3"
import { t } from "i18next"
import { getRequester } from "../../../../../functions/FilterUsers"
import { getLevels } from "../quadrants/utils/evaluationLevel"

const defaultColor = "#E0E0E0"
const requesterLevel = "#E8F4B3"
const evalutorLevel = "#1F77B4"
const neutralCard = "#B3F4EF"
const negativeCard = "#FFA8DB"
const actionCard = "#FFE299"
const positiveCard = "#B3EFBD"
const feelingCard = "#DABEF8"
const fillCircle = "#E8F4B3"
const cardEmptyColor = "pink"

export const getDefaultColor = (): string => {
  return defaultColor
}

export const getCardEmptyColor = (): string => {
  return cardEmptyColor
}

export const getCircleColor = (card?: ICard, users?: IUser[]): string => {
  const requester = getRequester(users)
  if (card?.owner && requester && card.owner === requester.cognito_id) {
    return "#FFA8DB"
  }

  return fillCircle
}

export const getRectColor = (card: ICard): string => {
  const category = card.category?.toLowerCase() ?? ""

  if (["roles", "activities", "expected-results"].includes(category)) {
    return neutralCard
  } else if (["apprentice", "practitioner", "expert", "craftman", "master"].includes(category)) {
    return requesterLevel || evalutorLevel
  } else if ("positive-points" === category) {
    return positiveCard
  } else if ("negative-points" === category) {
    return negativeCard
  } else if ("improvement-actions" === category) {
    return actionCard
  } else if ("feelings" === category) {
    return feelingCard
  }

  return defaultColor
}

export const isColliding = (origin: SVGGElement, target: SVGGElement): boolean => {
  if (!origin) return false

  const originRect = d3.select(origin).node()?.getBoundingClientRect() as DOMRect
  const targetRect = d3.select(target).node()?.getBoundingClientRect() as DOMRect

  if (targetRect && originRect) {
    const collided = !(
      targetRect.right < originRect.left ||
      targetRect.left > originRect.right ||
      targetRect.bottom < originRect.top ||
      targetRect.top > originRect.bottom
    )

    if (collided) {
      return true
    }

    return false
  }

  return false
}

export const getPostitCategoryString = (category: string | undefined | null): string => {
  switch (category) {
    case "roles":
      return t("board.title.roles")
    case "activities":
      return t("board.title.activities")
    case "expected-results":
      return t("board.title.expected-results")
    case "positive-points":
      return t("board.title.positive-points")
    case "negative-points":
      return t("board.title.negative-points")
    case "improvement-actions":
      return t("board.title.improvement-actions")
    case "feelings":
      return t("board.title.feelings")
    case "add":
      return t("feedback.form.fields.add", { defaultValue: "Adicionar" })
    case "trash":
      return t("buttonActions.delete", { defaultValue: "Excluir" })
    default:
      return t("board.title.unknown", { defaultValue: "Desconhecido" })
  }
}

export const getCategoryLabel = (category: string, levels: ILevel) => {
  const level = getLevels(levels)
  return level[category as keyof typeof level] as string
}

export const getRandomD3Ease = (): ((normalizedTime: number) => number) => {
  const eases: Array<(normalizedTime: number) => number> = [
    d3.easeLinear,
    d3.easeCubic,
    d3.easeCubicIn,
    d3.easeCubicOut,
    d3.easeCubicInOut,
    d3.easeBounce,
    d3.easeBounceIn,
    d3.easeBounceOut,
    d3.easeBounceInOut,
    d3.easeElastic,
    d3.easeElasticIn,
    d3.easeElasticOut,
    d3.easeElasticInOut,
    d3.easeQuad,
    d3.easeQuadIn,
    d3.easeQuadOut,
    d3.easeQuadInOut,
    d3.easeCircle,
    d3.easeCircleIn,
    d3.easeCircleOut,
    d3.easeCircleInOut,
    d3.easeSin,
    d3.easeSinIn,
    d3.easeSinOut,
    d3.easeSinInOut,
    d3.easeExp,
    d3.easeExpIn,
    d3.easeExpOut,
    d3.easeExpInOut,
    d3.easeBack,
    d3.easeBackIn,
    d3.easeBackOut,
    d3.easeBackInOut,
    d3.easePoly,
    d3.easePolyIn,
    d3.easePolyOut,
    d3.easePolyInOut,
  ]
  const idx = Math.floor(Math.random() * eases.length)
  return eases[idx]
}

export const getRandomDurationValue = (): number => {
  return Math.floor(Math.random() * (2000 - 500 + 1)) + 500
}
